import * as React from 'react';
import { AGE_GROUPS, SCHEDULES } from '../data';
import { Icon } from '../icons.jsx';
import { Badge, Button, Field, Input, Select, Toggle } from '../ui.jsx';

const availabilityCopy = {
  live: {
    tone: 'open',
    label: 'Current availability',
    description: 'Confirmed by the provider within the last seven days.',
  },
  stale: {
    tone: 'wait',
    label: 'Availability needs confirmation',
    description: 'Contact the provider before relying on previous opening counts.',
  },
  paused: {
    tone: 'neutral',
    label: 'Availability paused',
    description: 'This provider is not currently publishing opening counts.',
  },
  not_provided: {
    tone: 'neutral',
    label: 'Live availability not provided',
    description: 'Contact the provider for current availability.',
  },
};

const AvailabilityBadge = ({ state }) => {
  const copy = availabilityCopy[state] || availabilityCopy.not_provided;
  return <Badge tone={copy.tone}>{copy.label}</Badge>;
};

const CareProviderCard = ({ item, go }) => (
  <article className="live-care-card">
    <div className="live-care-card-heading">
      <div>
        <AvailabilityBadge state={item.availability.state}/>
        <h2>{item.provider.facilityName}</h2>
        <p>
          <Icon name="pin" size={13}/>
          {item.provider.address.line1}, {item.provider.address.city},{' '}
          {item.provider.address.zipCode}
        </p>
      </div>
      {item.totalOpenings !== null && (
        <div className="live-care-opening-total">
          <strong>{item.totalOpenings}</strong>
          <span>{item.totalOpenings === 1 ? 'opening' : 'openings'}</span>
        </div>
      )}
    </div>
    <div className="live-care-official-row">
      <span><Icon name="shield" size={14}/>Official Pennsylvania data</span>
      <span>{item.provider.officialDetails.starLevel || 'No STAR level'}</span>
      <span>
        {item.provider.programs.subsidyAgreement
          ? 'Subsidy agreement'
          : 'No subsidy agreement shown'}
      </span>
    </div>
    {item.classrooms.length > 0 && (
      <div className="live-care-classrooms">
        {item.classrooms.map((classroom) => (
          <span key={classroom.id}>
            <strong>{classroom.ageGroup}</strong>
            {item.availability.state === 'live'
              ? `${classroom.openings} open`
              : 'Contact to confirm'}
          </span>
        ))}
      </div>
    )}
    <div className="live-care-card-footer">
      <small>{availabilityCopy[item.availability.state]?.description}</small>
      <Button
        variant="secondary"
        size="sm"
        iconRight="chev-right"
        onClick={() =>
          go('pilot-profile', { providerId: item.provider.id })
        }
      >
        View record
      </Button>
    </div>
  </article>
);

export const LiveCareResultsScreen = ({ go, query }) => {
  const [zipCode, setZipCode] = React.useState(query.zip || '');
  const [search, setSearch] = React.useState('');
  const [ageGroup, setAgeGroup] = React.useState(
    query.childrenAges?.[0] || 'Young Toddler',
  );
  const [schedule, setSchedule] = React.useState(
    query.schedule || 'Full time',
  );
  const [subsidyOnly, setSubsidyOnly] = React.useState(false);
  const [currentOnly, setCurrentOnly] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        ageGroup,
        schedule,
        subsidyOnly: String(subsidyOnly),
        currentOpeningsOnly: String(currentOnly),
        limit: '25',
      });
      if (zipCode) params.set('zipCode', zipCode);
      if (search.trim()) params.set('search', search.trim());
      const response = await fetch(`/api/care-providers?${params}`);
      const body = await response.json();
      if (!response.ok) throw new Error(body.error?.message || 'Search failed');
      setResult(body);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [ageGroup, currentOnly, schedule, search, subsidyOnly, zipCode]);

  React.useEffect(() => {
    load();
  }, []);

  return (
    <div className="live-care-page route-in">
      <section className="live-care-hero">
        <div className="container">
          <Badge tone="brand" icon="shield">Production pilot records</Badge>
          <h1>Find care using official records and provider updates</h1>
          <p>
            Official identity comes from Pennsylvania. Opening counts appear only
            when participating providers publish and confirm them.
          </p>
          <form
            className="live-care-search"
            onSubmit={(event) => {
              event.preventDefault();
              load();
            }}
          >
            <Field label="ZIP code">
              <Input
                value={zipCode}
                onChange={(event) =>
                  setZipCode(
                    event.target.value.replace(/\D/g, '').slice(0, 5),
                  )
                }
                placeholder="19130"
                inputMode="numeric"
              />
            </Field>
            <Field label="Facility name">
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Optional"
              />
            </Field>
            <Field label="Age group">
              <Select
                value={ageGroup}
                onChange={setAgeGroup}
                options={AGE_GROUPS}
              />
            </Field>
            <Field label="Schedule">
              <Select
                value={schedule}
                onChange={setSchedule}
                options={SCHEDULES}
              />
            </Field>
            <Button type="submit" icon="search">Search</Button>
          </form>
          <div className="live-care-filters">
            <Toggle
              value={subsidyOnly}
              onChange={setSubsidyOnly}
              label="Subsidy agreement"
            />
            <Toggle
              value={currentOnly}
              onChange={setCurrentOnly}
              label="Current openings only"
            />
          </div>
        </div>
      </section>
      <section className="container live-care-content">
        {error && <div className="provider-access-error" role="alert">{error}</div>}
        <div className="live-care-results-heading">
          <div>
            <span>Philadelphia pilot</span>
            <h2>
              {loading
                ? 'Searching official records…'
                : `${result?.meta.count || 0} providers shown`}
            </h2>
          </div>
          <button type="button" onClick={() => go('results')}>
            View illustrative prototype
          </button>
        </div>
        {!loading && result?.data.length ? (
          <div className="live-care-list">
            {result.data.map((item) => (
              <CareProviderCard
                key={item.provider.id}
                item={item}
                go={go}
              />
            ))}
          </div>
        ) : !loading && !error ? (
          <div className="provider-claim-empty">
            <Icon name="search" size={24}/>
            <strong>No providers match this production search</strong>
            <span>Try another ZIP, age group, or availability filter.</span>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export const LiveCareProfileScreen = ({ go, providerId }) => {
  const [item, setItem] = React.useState(null);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let alive = true;
    fetch(`/api/care-providers/${providerId}`)
      .then(async (response) => {
        const body = await response.json();
        if (!response.ok) {
          throw new Error(body.error?.message || 'Provider could not be loaded');
        }
        if (alive) setItem(body.data);
      })
      .catch((requestError) => {
        if (alive) setError(requestError.message);
      });
    return () => {
      alive = false;
    };
  }, [providerId]);

  if (error) {
    return (
      <div className="provider-access-state is-error">
        <h1>Provider unavailable</h1>
        <p>{error}</p>
        <Button variant="secondary" onClick={() => go('pilot-results')}>
          Return to search
        </Button>
      </div>
    );
  }
  if (!item) {
    return <div className="provider-access-state">Loading official provider record…</div>;
  }
  const copy =
    availabilityCopy[item.availability.state] ||
    availabilityCopy.not_provided;
  return (
    <div className="live-care-profile-page route-in">
      <header className="live-care-profile-header">
        <div className="container">
          <button type="button" onClick={() => go('pilot-results')}>
            <Icon name="chev-left" size={15}/> Production search
          </button>
          <AvailabilityBadge state={item.availability.state}/>
          <h1>{item.provider.facilityName}</h1>
          <p>
            {item.provider.address.line1}, {item.provider.address.city},{' '}
            {item.provider.address.state} {item.provider.address.zipCode}
          </p>
        </div>
      </header>
      <main className="container live-care-profile-content">
        <section className="live-care-profile-official">
          <div className="live-workspace-panel-heading">
            <div>
              <span>Official Pennsylvania data</span>
              <h2>Program record</h2>
            </div>
            <Badge tone="brand" icon="shield">Read-only</Badge>
          </div>
          <dl>
            <div><dt>Provider type</dt><dd>{item.provider.providerType || 'Not provided'}</dd></div>
            <div><dt>License number</dt><dd>{item.provider.officialDetails.licenseNumber || 'Not provided'}</dd></div>
            <div><dt>Licensed capacity</dt><dd>{item.provider.officialDetails.capacity ?? 'Not provided'}</dd></div>
            <div><dt>Keystone STARS</dt><dd>{item.provider.officialDetails.starLevel || 'No STAR level'}</dd></div>
            <div><dt>ELRC</dt><dd>{item.provider.officialDetails.elrc || 'Not provided'}</dd></div>
            <div><dt>Subsidy</dt><dd>{item.provider.programs.subsidyAgreement ? 'Agreement shown' : 'No agreement shown'}</dd></div>
          </dl>
        </section>
        <section className="live-care-profile-provider">
          <div className="live-workspace-panel-heading">
            <div>
              <span>Provider supplied</span>
              <h2>Current family information</h2>
            </div>
            <AvailabilityBadge state={item.availability.state}/>
          </div>
          <div className="live-care-status-note">
            <strong>{copy.label}</strong>
            <p>{copy.description}</p>
          </div>
          {item.profile ? (
            <dl>
              <div><dt>Hours</dt><dd>{item.profile.hours}</dd></div>
              <div><dt>Languages</dt><dd>{item.profile.languages.join(', ')}</dd></div>
              <div><dt>Curriculum</dt><dd>{item.profile.curriculumSummary}</dd></div>
              <div>
                <dt>Tour options</dt>
                <dd>
                  {[
                    item.profile.acceptsInPersonTours && 'In person',
                    item.profile.acceptsVirtualTours && 'Virtual',
                  ].filter(Boolean).join(', ') || 'Contact provider'}
                </dd>
              </div>
            </dl>
          ) : (
            <div className="provider-claim-empty">
              <strong>Provider profile information not supplied</strong>
            </div>
          )}
          {item.classrooms.length > 0 && (
            <div className="live-care-profile-classrooms">
              {item.classrooms.map((classroom) => (
                <article key={classroom.id}>
                  <div>
                    <strong>{classroom.name}</strong>
                    <span>{classroom.ageGroup} · {classroom.schedule}</span>
                  </div>
                  <div>
                    <strong>
                      {item.availability.state === 'live'
                        ? classroom.openings
                        : 'Confirm'}
                    </strong>
                    <span>openings</span>
                  </div>
                </article>
              ))}
            </div>
          )}
          <div className="live-care-contact">
            <p>
              Tours and waitlists become production actions in P3. For now,
              contact the provider using the official public phone.
            </p>
            {item.provider.contact.phone && (
              <a href={`tel:${item.provider.contact.phone}`}>
                <Icon name="phone" size={15}/>
                Call {item.provider.contact.phone}
              </a>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};
